import type { AsyncValueCallback, Data, DataKey, DataSchema, Optional } from "shelving";
import { createContext, useContext } from "react";
import { BooleanStore, DataStore, DictionaryStore, Feedback, Feedbacks, NONE, Schema, StateError, ValueError, getRandomKey } from "shelving";
import { useInstance, useLazy } from "shelving/react";
import { closeParentDialog } from "../base/Dialogs.js";

/** Context for current form. */
const FORM_CONTEXT = createContext<FormStore<Data> | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any

/** Hook that allows you to access the current state of a `<Form />` element. */
export function useFormContext<T extends Data = Data>(): FormStore<T>;
export function useFormContext(): FormStore<Data>;
export function useFormContext(): FormStore<Data> {
	const state = useContext(FORM_CONTEXT);
	if (!state) throw new StateError("useForm() must be used inside <Form>");
	return state;
}

/** Hook that allows you to access the current state of a `<Form />` element, if it exists. */
export function useOptionalFormContext<T extends Data = Data>(): FormStore<T> | null;
export function useOptionalFormContext(): FormStore<Data> | null;
export function useOptionalFormContext(): FormStore<Data> | null {
	return useContext(FORM_CONTEXT);
}

/** Work with `<input>`, `<select>`, or `<textarea>` etc */
export type HTMLControlElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** Props for a `<Form>` element */
export type FormProps = { children: React.ReactNode; className?: string | undefined };

/** Store the current value of a form. */
export class FormStore<T extends Data = Data> extends DataStore<Partial<T>> {
	/** Unique ID for the form. */
	readonly id = getRandomKey();

	/** Schema for the current form. */
	readonly schema: DataSchema<T>;

	/** State listing the busy state. */
	readonly busy: BooleanStore = new BooleanStore();

	/** State listing feedback messages. */
	readonly feedback: DictionaryStore<string> = new DictionaryStore();

	/** Get the current validated value for this form. */
	get validated(): T {
		return this.schema.validate(this.value);
	}

	/** Store the most recently submitted value for this form. */
	readonly submitted: DataStore<T> = new DataStore<T>(NONE);

	constructor(schema: DataSchema<T>, value: Partial<T> = {}) {
		super(value);
		this.schema = schema;
	}

	private async _submit(onSubmit: Optional<AsyncValueCallback<T>>, form?: HTMLFormElement) {
		if (this.busy.value) return; // Don't re-submit if form is busy.
		this.feedback.set({});
		this.busy.set(true);
		try {
			const data = this.validated;
			if (onSubmit) await onSubmit(data);
			this.submitted.value = data;
			if (form) closeParentDialog(form);
		} catch (thrown) {
			console.error("FORM ERROR:", thrown); // eslint-disable-line no-console
			if (thrown instanceof Feedbacks) this.feedback.set(thrown.messages);
		} finally {
			this.busy.set(false);
		}
	}

	/** Get a named schema for a field of this form. */
	getSchema<K extends DataKey<T>>(name: K): Schema<T[K]> {
		const schema = this.schema.props[name];
		if (schema instanceof Schema) return schema as Schema<T[K]>;
		throw new ValueError("Schema does not exist in form", name);
	}

	/** Publish a value for a field of this form. */
	publish<K extends DataKey<T>>(name: K, unsafeValue: unknown): void {
		try {
			this.setProp(name, this.getSchema(name).validate(unsafeValue));
			this.feedback.delete(name);
		} catch (thrown) {
			console.error("FORM ERROR:", thrown); // eslint-disable-line no-console
			if (thrown instanceof Feedback) this.feedback.setItem(name, thrown.message);
			else if (typeof thrown === "string") this.feedback.setItem(name, thrown);
			else this.feedback.setItem(name, "Unknown error");
		}
	}

	/** Output the `<form>` for this `FormState` */
	readonly Form = ({
		onSubmit, //
		mods = "",
		children,
	}: {
		onSubmit?: AsyncValueCallback<T> | undefined;
		mods?: string | undefined;
		children: React.ReactNode;
	}): React.ReactElement => {
		return (
			<form
				className={`form ${mods}`}
				onSubmit={e => {
					e.preventDefault();
					void this._submit(onSubmit, e.currentTarget);
				}}
				noValidate
			>
				<FORM_CONTEXT.Provider value={this as FormStore<Data>}>{children}</FORM_CONTEXT.Provider>
			</form>
		);
	};
}

/**
 * Use a new form.
 * @param data Initial data for the form (only used on first render).
 */
export function useForm<T extends Data>(schema: DataSchema<T>, data?: Partial<T>): FormStore<T> {
	return useInstance(FormStore, schema, useLazy(data));
}

/**
 * Create and provide and output a form.
 * - Provides form context to the child components (e.g. fields) so they can call `useForm()`
 */
export function Form<T extends Data>({
	schema, //
	data,
	onSubmit,
	mods,
	children,
}: {
	schema: DataSchema<T>;
	data?: Partial<T> | undefined;
	onSubmit?: AsyncValueCallback<T> | undefined;
	mods?: string | undefined;
	children: React.ReactNode;
}): React.ReactElement {
	const form = useForm(schema, data);
	return (
		<form.Form mods={mods} onSubmit={onSubmit}>
			{children}
		</form.Form>
	);
}
